import { forwardRef, type InputHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./lib/cn.js";

/**
 * Input — chrome-vocabulary text field.
 *
 * Sharp corners + hairline border + mono body text. Focus paints the
 * border with the primary accent rather than adding a Tailwind ring,
 * which composes cleanly with the chamfered chrome layer of a
 * surrounding Panel / Card.
 */
const inputVariants = cva(
  [
    "flex w-full bg-transparent",
    "font-[var(--pp-font-family)] text-[var(--pp-text)] placeholder:text-[var(--pp-text-muted)]",
    "border border-[var(--pp-border)]",
    "transition-[border-color,box-shadow]",
    "hover:border-[var(--pp-text-muted)]",
    "focus-visible:outline-none focus-visible:border-[var(--primary)]",
    "focus-visible:ring-1 focus-visible:ring-[var(--primary)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "file:border-0 file:bg-transparent file:text-[var(--pp-text)] file:font-medium",
  ],
  {
    variants: {
      variant: {
        default: "",
        error:
          "border-[var(--pp-danger)] hover:border-[var(--pp-danger)] focus-visible:border-[var(--pp-danger)] focus-visible:ring-[var(--pp-danger)]",
      },
      size: {
        sm: "h-7 px-2 text-[var(--pp-font-sm)]",
        default: "h-9 px-3 text-[var(--pp-font-md)]",
        lg: "h-11 px-4 text-[var(--pp-font-lg)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  variant?: "default" | "error";
  size?: "sm" | "default" | "lg";
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, type = "text", ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      data-pp-input
      data-pp-variant={variant ?? "default"}
      className={cn(inputVariants({ variant, size }), className)}
      {...props}
    />
  ),
);

Input.displayName = "Input";

export { inputVariants };
